import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlatformAuthProvider, usePlatformAuth } from "@/context/PlatformAuthContext";
import AppShell from "@/components/AppShell";
import PlatformShell from "@/components/PlatformShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Documents from "@/pages/Documents";
import InvoiceEditor from "@/pages/InvoiceEditor";
import Quotations from "@/pages/Quotations";
import Challans from "@/pages/Challans";
import InvoiceView from "@/pages/InvoiceView";
import Parties from "@/pages/Parties";
import Purchases from "@/pages/Purchases";
import PurchaseBillView from "@/pages/PurchaseBillView";
import PartyProfile from "@/pages/PartyProfile";
import PartyLedger from "@/pages/PartyLedger";
import Items from "@/pages/Items";
import Inventory from "@/pages/Inventory";
import StockReceipt from "@/pages/StockReceipt";
import Banking from "@/pages/Banking";
import Team from "@/pages/Team";
import Billing from "@/pages/Billing";
import AcceptInvite from "@/pages/AcceptInvite";
import Payments from "@/pages/Payments";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import PublicInvoice from "@/pages/PublicInvoice";
import PlatformLogin from "@/pages/platform/PlatformLogin";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import Organizations from "@/pages/platform/Organizations";
import OrganizationDetail from "@/pages/platform/OrganizationDetail";
import Plans from "@/pages/platform/Plans";
import Modules from "@/pages/platform/Modules";
import Announcements from "@/pages/platform/Announcements";
import Admins from "@/pages/platform/Admins";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import "@/App.css";

const Protected = ({ children }) => {
  const { loading, user } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-sm text-slate-500">Loading your books…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <AppShell>{children}</AppShell>;
};

/** Where an already-signed-in visitor to /login should go. Honours the ?next=
 *  the 401 interceptor sets, so a dropped session resumes its page. This guard
 *  fires the moment auth state flips, so it — not the Login component — decides
 *  the destination. */
const useNextPath = (fallback, prefix = "/") => {
  const [params] = useSearchParams();
  const next = params.get("next");
  return next && next.startsWith(prefix) ? next : fallback;
};

const LoginRoute = () => {
  const { loading, user } = useAuth();
  const target = useNextPath("/dashboard");
  if (loading) return <div className="min-h-screen grid place-items-center bg-slate-50 text-sm text-slate-500">Loading…</div>;
  if (user) return <Navigate to={target} replace />;
  return <Login />;
};

/** Tenant app — everything a business user sees. */
const TenantApp = () => (
  <AuthProvider>
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/v/:token" element={<PublicInvoice />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/invoices" element={<Protected><Documents /></Protected>} />
      <Route path="/invoices/new" element={<Protected><InvoiceEditor /></Protected>} />
      <Route path="/invoices/:id" element={<Protected><InvoiceView /></Protected>} />
      <Route path="/invoices/:id/edit" element={<Protected><InvoiceEditor /></Protected>} />
      <Route path="/quotations" element={<Protected><Quotations /></Protected>} />
      <Route path="/quotations/new" element={<Protected><InvoiceEditor mode="QUOTATION" /></Protected>} />
      <Route path="/quotations/:id/edit" element={<Protected><InvoiceEditor mode="QUOTATION" /></Protected>} />
      <Route path="/proforma/new" element={<Protected><InvoiceEditor mode="PROFORMA" /></Protected>} />
      <Route path="/challans" element={<Protected><Challans /></Protected>} />
      <Route path="/challans/new" element={<Protected><InvoiceEditor mode="CHALLAN" /></Protected>} />
      <Route path="/challans/:id/edit" element={<Protected><InvoiceEditor mode="CHALLAN" /></Protected>} />
      <Route path="/recurring/new" element={<Protected><InvoiceEditor mode="RECURRING" /></Protected>} />
      <Route path="/recurring/:id/edit" element={<Protected><InvoiceEditor mode="RECURRING" /></Protected>} />
      <Route
        path="/credit-notes"
        element={<Protected><Documents docType="CREDIT_NOTE" title="Credit Notes" /></Protected>}
      />
      <Route path="/payments" element={<Protected><Payments /></Protected>} />
      {/* Keyed because both routes render the same component: without it React
          reuses the instance and only the prop changes. The page handles that
          correctly now, but the key makes a clean slate the default rather
          than something each such page has to remember. */}
      <Route path="/customers" element={<Protected><Parties key="CUSTOMER" kind="CUSTOMER" /></Protected>} />
      <Route path="/suppliers" element={<Protected><Parties key="SUPPLIER" kind="SUPPLIER" /></Protected>} />
      <Route path="/purchases" element={<Protected><Purchases /></Protected>} />
      <Route path="/purchases/bills/:id" element={<Protected><PurchaseBillView /></Protected>} />
      <Route path="/parties/:id" element={<Protected><PartyProfile /></Protected>} />
      {/* Old single-list route, kept so existing links and bookmarks still work. */}
      <Route path="/parties" element={<Navigate to="/customers" replace />} />
      <Route path="/parties/:id/ledger" element={<Protected><PartyLedger /></Protected>} />
      <Route path="/items" element={<Protected><Items /></Protected>} />
      <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
      <Route path="/inventory/receive" element={<Protected><StockReceipt /></Protected>} />
      <Route path="/banking" element={<Protected><Banking /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  </AuthProvider>
);

const PlatformProtected = ({ children }) => {
  const { loading, admin } = usePlatformAuth();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#14121f]">
        <div className="text-sm text-slate-400">Loading console…</div>
      </div>
    );
  if (!admin) return <Navigate to="/platform/login" replace />;
  return <PlatformShell>{children}</PlatformShell>;
};

const PlatformLoginRoute = () => {
  const { loading, admin } = usePlatformAuth();
  // Console paths only: a stale next must never cross the two sessions.
  const target = useNextPath("/platform", "/platform");
  if (loading) return <div className="min-h-screen grid place-items-center bg-[#14121f] text-sm text-slate-400">Loading…</div>;
  if (admin) return <Navigate to={target} replace />;
  return <PlatformLogin />;
};

/**
 * Superadmin console — its own provider so a tenant session and a platform
 * session never observe each other's state.
 */
const PlatformApp = () => (
  <PlatformAuthProvider>
    <Routes>
      <Route path="login" element={<PlatformLoginRoute />} />
      <Route index element={<PlatformProtected><PlatformDashboard /></PlatformProtected>} />
      <Route path="organizations" element={<PlatformProtected><Organizations /></PlatformProtected>} />
      <Route path="organizations/:id" element={<PlatformProtected><OrganizationDetail /></PlatformProtected>} />
      <Route path="plans" element={<PlatformProtected><Plans /></PlatformProtected>} />
      <Route path="modules" element={<PlatformProtected><Modules /></PlatformProtected>} />
      <Route path="announcements" element={<PlatformProtected><Announcements /></PlatformProtected>} />
      <Route path="admins" element={<PlatformProtected><Admins /></PlatformProtected>} />
      <Route path="audit" element={<PlatformProtected><PlatformAudit /></PlatformProtected>} />
      <Route path="*" element={<Navigate to="/platform" replace />} />
    </Routes>
  </PlatformAuthProvider>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/platform/*" element={<PlatformApp />} />
        <Route path="/*" element={<TenantApp />} />
      </Routes>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
